"use client";

import Image from "next/image";
import { SMART_MANAGER_LOGO_URL } from "@/app/components/public/landing/homepageAssets";
import { orderLandingDesktopDownloadItems } from "@/app/components/public/landing/landingDownloadOrder";
import type { LandingDesktopDownloadMenu } from "@/app/components/public/landing/LandingDesktopNavigation";
import {
  getNativeLanguageName,
  LandingNavWindowsContext,
  type LandingNavWindowsData,
} from "@/app/components/public/landing/LandingNavWindowsContext";
import { useAmbientPulseRings } from "@/src/features/visualization/ambient/useAmbientPulseRings";
import type { SphereAudioState } from "@smartmanager/shell-ui/host";
import LandingHeroCopy, { useHeroCopyHeight } from "@/app/components/public/landing/LandingHeroCopy";
import { LandingCompanyInfo } from "@/app/components/public/landing/LandingFooterDisclosure";
import { introOfferNote } from "@/src/features/public/introOffer";
import type { LandingWatchAction } from "@/app/components/public/landing/WatchVideoButton";
import { DEFAULT_LANDING_FILM, type LandingFilmId } from "@/src/features/landing/filmLibrary";
import { PRIVACY_PROMISE } from "@/app/components/public/landing/privacyPromise";
import {
  LandingNebulaFallback,
  LandingSceneFallback,
  NeuralSphereFallback,
} from "@/app/components/public/landing/LandingHeroFallbacks";
import { useMobileDetection } from "@/app/hooks/useMobileDetection";
import {
  detectClientDownloadEnvironment,
  type DesktopClientPlatform,
} from "@/src/features/downloads/clientPlatform";
import {
  parseDesktopDownloadResponse,
  type DesktopDownloadPlatform,
} from "@/src/features/downloads/desktopDownloads";
import { resolveConfiguredMobileDownloadUrl } from "@/src/features/downloads/mobileDownloads";
import { bindPlatformUrl } from "@smartmanager/shell-ui/shared/browser/boundUrlSet";
import { usePublicFrontendTranslation } from "@smartmanager/shell-ui/shared/i18n/usePublicFrontendTranslation";
import { signedOutErrorMessage } from "@/src/shared/auth/abuseRejectionMessages";
import { DASHBOARD_PATH } from "@/src/features/auth/desktopEntry";
import { broadcastLogin } from "@/src/shared/browser/crossWindowSync";
import { setLoginEntry } from "@/src/shared/browser/loginEntry";
import { clearSession, commitElectronLoginSession, hasBrowserSession } from "@/src/shared/browser/sessionStorage";
import {
  CONTINUITY_DEEP_LINK_STORAGE_KEY,
  parseContinuityDeepLink,
} from "@/src/features/jobs/continuity";
import {
  clearLandingLocaleContinuity,
  consumeLandingLocaleContinuity,
  persistLandingLocaleContinuity,
} from "@/src/features/landing/localeContinuity";
import { requestLandingContactDraftContinuity } from "@/src/features/landing/contactDraftContinuity";
import { isLandingFooterRailCollapsed, isLandingWorkspaceAction } from "@/src/features/landing/workspaceActionState";
import { DESKTOP_HERO_PROVISIONAL_COPY_CSS, getDesktopHeroLayout } from "@/src/features/landing/desktopHeroLayout";
import { isElectron, openAuthPopup } from "@/src/shared/platform";
import { API_BASE_URL } from "@/src/shared/config/api";
import { buildWorkosAuthorizeUrl, parseSafeWorkosAuthorizationUrl } from "@/src/shared/auth/workosAuthorizeUrl";
import type {
  LandingWorkspaceAction,
  LandingWorkspaceCommand,
} from "@/app/components/public/landing/LandingPublicWorkspace";
import { useLocale, useLocales, useSetLocale } from "@smartmanager/shell-ui/shared/i18n/i18nContext";
import { windowFocusReturnTarget } from "@smartmanager/shell-ui/features/windows/windowFocusReturn";
import { LandingErrorBanner } from "@/app/components/public/landing/LandingErrorBanner";
import { CONSENT_BAR_RESERVE_BOTTOM, useConsentBarReserve } from "@/src/features/consent/consentBarReserve";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// The store links are build-time settings (NEXT_PUBLIC_*, inlined by the
// bundler), so they are code, not data: bound here so the download panel can
// open them (boundUrlSet.ts bindPlatformUrl).
function bindMobileDownloadUrl(platformUrl: string | undefined, genericUrl: string | undefined): string | null {
  const url = resolveConfiguredMobileDownloadUrl(platformUrl, genericUrl);
  return url ? bindPlatformUrl(url) : null;
}
const IOS_DOWNLOAD_URL = bindMobileDownloadUrl(
  process.env.NEXT_PUBLIC_IOS_APP_DOWNLOAD_URL,
  process.env.NEXT_PUBLIC_MOBILE_APP_DOWNLOAD_URL,
);
const ANDROID_DOWNLOAD_URL = bindMobileDownloadUrl(
  process.env.NEXT_PUBLIC_ANDROID_APP_DOWNLOAD_URL,
  process.env.NEXT_PUBLIC_MOBILE_APP_DOWNLOAD_URL,
);

// The mobile and desktop landings are separate chunks, still server-rendered:
// a page downloads only the layout it renders, and SSR preloads that chunk.
const MobileOrbLanding = dynamic(
  () => import("@/app/components/public/landing/MobileOrbLanding"),
);
const LandingDesktopNavigation = dynamic(
  () => import("@/app/components/public/landing/LandingDesktopNavigation"),
);
const IntegrationProviderRail = dynamic(
  () => import("@/app/components/public/landing/IntegrationProviderRail"),
);

const LandingPublicWorkspace = dynamic(
  () => import("@/app/components/public/landing/LandingPublicWorkspace"),
  { ssr: false, loading: () => null }
);

// Best-effort warm-up for the workspace chunk (and, given an action, that
// window's chunk). A failed fetch is swallowed here: the bundler does not cache
// failed chunk loads, so the real open retries the import and surfaces its own error.
function prefetchLandingWorkspace(action?: LandingWorkspaceAction): void {
  import("@/app/components/public/landing/LandingPublicWorkspace")
    .then((workspace) => {
      if (action) workspace.preloadLandingWorkspaceAction(action);
    })
    .catch(() => {});
}

const ThreeDNebula = dynamic(() => import("./ThreeDNebula"), {
  ssr: false,
  loading: () => <LandingNebulaFallback />,
});

// The desktop hero shows the platform's own neural sphere, with the hero copy under it.
const HeroNeuralSphere = dynamic(
  () => import("@/app/components/public/landing/HeroNeuralSphere"),
  { ssr: false, loading: () => <NeuralSphereFallback /> }
);

const HomeHeroScene = dynamic(
  () => import("@/app/components/public/landing/HomeHeroScene"),
  { ssr: false, loading: () => <LandingSceneFallback /> }
);

type HeroAction = {
  label: string;
  onClick: () => void | Promise<void>;
  actionKey: LandingWorkspaceAction;
  disabled?: boolean;
  ariaLabel?: string;
  /** The intro offer, shown under the tab's label (Pricing). */
  offer?: string;
};

const clampValue = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  ru: "Russian",
  ar: "Arabic",
  hi: "Hindi",
};

const normalizeLanguageCode = (value?: string | null): string => {
  if (!value) return "en";
  return value.split("-")[0].toLowerCase();
};

// The Language button stays hidden until every film has its 11 non-English
// narrations recorded and reviewed (only the English takes are approved).
// TODO: turn this back on once the remaining films are translated.
const LANDING_LANGUAGE_PICKER_ENABLED = false;

const PUBLIC_LANDING_REQUEST_TIMEOUT_MS = 15_000;
// The full desktop navigation needs roughly 930px once every action is visible.
// Keep tablet-sized windows on the purpose-built compact landing surface.
export const LANDING_DESKTOP_MIN_WIDTH = 1024;

// A narrow or touch browser that the server rendered as desktop switches to
// the mobile landing right after hydration; start fetching it now.
if (
  typeof window !== "undefined"
  && (window.innerWidth < LANDING_DESKTOP_MIN_WIDTH || window.matchMedia?.("(pointer: coarse)").matches)
) {
  void import("@/app/components/public/landing/MobileOrbLanding");
}

/**
 * "/" is a cacheable static page, so browsers may restore it from the
 * back/forward cache after the WorkOS redirect. Run `reset` on such restores so
 * in-flight auth state (buttons stuck on "Authorizing...") does not survive.
 */
export function onBackForwardCacheRestore(target: Pick<EventTarget, "addEventListener" | "removeEventListener">, reset: () => void): () => void {
  const handlePageShow = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) reset();
  };
  target.addEventListener("pageshow", handlePageShow);
  return () => target.removeEventListener("pageshow", handlePageShow);
}

export function getLandingRequestErrorMessage(error: unknown, fallback: string): string {
  if (
    error instanceof Error
    && error.name !== "AbortError"
    && error.name !== "TimeoutError"
    && error.message
  ) {
    return error.message;
  }
  return fallback;
}

type SessionStorageWriter = Pick<Storage, "setItem">;

export function persistContinuityDashboardPath(
  dashboardPath: string,
  getStorage: () => SessionStorageWriter = () => window.sessionStorage,
): boolean {
  try {
    getStorage().setItem(CONTINUITY_DEEP_LINK_STORAGE_KEY, dashboardPath);
    return true;
  } catch {
    return false;
  }
}

export function getDesktopDownloadFailureMessage(status: number): string {
  return status === 503
    ? "Desktop download is temporarily unavailable. Please try again later."
    : "Download failed. Please try again.";
}

type HomePageClientProps = {
  initialIsMobile: boolean;
};

export default function Home3({ initialIsMobile }: HomePageClientProps) {
  const { isMobile } = useMobileDetection();
  const t = usePublicFrontendTranslation();
  const locale = useLocale();
  const supportedLocales = useLocales();
  const setLocale = useSetLocale();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const message = signedOutErrorMessage(error, t);
    if (message) {
      setErrorMessage(message);
      params.delete("error");
      const query = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    }
  }, [t]);
  const [detectedOS, setDetectedOS] = useState<DesktopClientPlatform>("unknown");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingPlatform, setDownloadingPlatform] = useState<DesktopDownloadPlatform | null>(null);
  const [downloadPanelError, setDownloadPanelError] = useState<string | null>(null);
  const [videoFilm, setVideoFilm] = useState<LandingFilmId>(DEFAULT_LANDING_FILM);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [viewportSize, setViewportSize] = useState(
    initialIsMobile ? { width: 390, height: 844 } : { width: 1360, height: 860 }
  );
  // The landing lays out above the consent bar while it is showing, never under it.
  const consentBarReserve = useConsentBarReserve();
  const layoutViewport = useMemo(
    () => ({ width: viewportSize.width, height: Math.max(0, viewportSize.height - consentBarReserve) }),
    [consentBarReserve, viewportSize],
  );
  const [workspaceMounted, setWorkspaceMounted] = useState(false);
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [workspaceCommand, setWorkspaceCommand] = useState<LandingWorkspaceCommand | null>(null);
  const workspaceCommandIdRef = useRef(0);
  const workspaceTriggerRefs = useRef<Partial<Record<LandingWorkspaceAction, HTMLElement>>>({});
  const lastVisibleWorkspaceActionRef = useRef<LandingWorkspaceAction | null>(null);
  const workspaceFocusRestoreFrameRef = useRef<number | null>(null);
  const restoredLocaleContinuityRef = useRef(false);
  const downloadInFlightRef = useRef(false);
  const authInFlightRef = useRef(false);
  const orbContainerRef = useRef<HTMLDivElement>(null);
  // The landing sphere has no voice; it only breathes.
  const heroVoiceRef = useRef<SphereAudioState>({ amplitude: 0, isPlaying: false });
  const heroCopy = useHeroCopyHeight();
  const ambientPulseFieldRef = useRef<HTMLDivElement>(null);
  // Landing errors stay until the visitor dismisses them or starts a new action.
  const showLandingError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);
  const dismissLandingError = useCallback(() => setErrorMessage(null), []);

  useEffect(() => () => {
    if (workspaceFocusRestoreFrameRef.current !== null) {
      window.cancelAnimationFrame(workspaceFocusRestoreFrameRef.current);
    }
  }, []);

  useEffect(() => {
    const continuityTarget = parseContinuityDeepLink(window.location.search);
    if (continuityTarget) {
      // A signed-out continuity handoff is only a best-effort convenience.
      // Privacy modes and embedded browsers can deny session storage; that
      // must not abort the rest of the public landing-page initialization.
      persistContinuityDashboardPath(continuityTarget.dashboardPath);
      if (hasBrowserSession()) {
        // in-app-browser exception: a first-party Smart Manager page in this same window, not a website.
        window.location.replace(continuityTarget.dashboardPath);
        return;
      }
    }

    void detectClientDownloadEnvironment().then((environment) => {
      setDetectedOS(environment.desktopPlatform);
    });
  }, []);

  // Before the first paint, so the hero is laid out for this window, not the server's guess.
  const [viewportMeasured, setViewportMeasured] = useState(false);
  useLayoutEffect(() => {
    const handleResize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    handleResize();
    setViewportMeasured(true);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const preload = () => {
      window.setTimeout(() => {
        const scheduleIdle =
          window.requestIdleCallback ??
          ((callback: IdleRequestCallback) => window.setTimeout(callback, 1));
        scheduleIdle(() => {
          prefetchLandingWorkspace();
        });
      }, 6000);
    };

    if (document.readyState === "complete") preload();
    else window.addEventListener("load", preload, { once: true });
    return () => window.removeEventListener("load", preload);
  }, []);

  const normalizedLocale = useMemo(() => normalizeLanguageCode(locale), [locale]);
  const languageOptions = useMemo(() => {
    const normalizedLocales = Array.from(
      new Set(supportedLocales.map((localeCode) => normalizeLanguageCode(localeCode)))
    );
    if (!normalizedLocales.includes("en")) normalizedLocales.unshift("en");
    return normalizedLocales
      .map((localeCode) => ({
        value: localeCode,
        label: t(LANGUAGE_LABELS[localeCode] || localeCode.toUpperCase()),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [supportedLocales, t]);

  const handleLanguageSelect = useCallback(async (code: string): Promise<boolean> => {
    const nextLanguage = normalizeLanguageCode(code);
    if (nextLanguage === normalizedLocale) return true;

    try {
      requestLandingContactDraftContinuity();
      persistLandingLocaleContinuity({
        activeAction: isLandingWorkspaceAction(activeAction) ? activeAction : null,
      });
      // No full reload: that closes the active window and discards drafts.
      // The provider remounts on web; the continuity state saved above
      // restores the open panel.
      setLocale(nextLanguage);
      return true;
    } catch (err) {
      console.error("Failed to update the interface language:", err);
      showLandingError(t("Failed to update language. Please try again."));
      return false;
    }
  }, [activeAction, normalizedLocale, setLocale, showLandingError, t]);

  const downloadDesktopArtifact = useCallback(async (
    platform: DesktopDownloadPlatform,
    options: { inline?: boolean } = {},
  ) => {
    if (downloadInFlightRef.current) return;
    downloadInFlightRef.current = true;
    setIsDownloading(true);
    setDownloadingPlatform(platform);
    setDownloadPanelError(null);

    const baseUrl = isElectron ? "https://www.smartmanager.ai" : "";
    const params = new URLSearchParams({
      target: "desktop",
      platform,
    });

    params.set("format", "json");
    try {
      const response = await fetch(`${baseUrl}/api/download?${params.toString()}`, {
        signal: AbortSignal.timeout(PUBLIC_LANDING_REQUEST_TIMEOUT_MS),
      });
      const data = await response.json().catch(() => ({}));
      const downloadUrl = response.ok
        ? parseDesktopDownloadResponse(data, platform, baseUrl || window.location.origin)
        : null;
      if (!downloadUrl) {
        throw new Error(t(getDesktopDownloadFailureMessage(response.status)));
      }
      // in-app-browser exception: the desktop installer is a file download for the OS, not a page.
      window.location.assign(downloadUrl);
    } catch (error) {
      const message = getLandingRequestErrorMessage(error, t("Download failed. Please try again."));
      // The Download window shows its own failure beside the verb; the page
      // banner stays for downloads started from anywhere else.
      if (options.inline) setDownloadPanelError(message);
      else showLandingError(message);
    } finally {
      downloadInFlightRef.current = false;
      setIsDownloading(false);
      setDownloadingPlatform(null);
    }
  }, [showLandingError, t]);

  const requestWorkspaceAction = useCallback((action: LandingWorkspaceAction) => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      workspaceTriggerRefs.current[action] = activeElement;
    }
    const toggleIfOpen = activeAction === action;
    setWorkspaceMounted(true);
    setWorkspaceCommand({
      id: ++workspaceCommandIdRef.current,
      action,
      toggleIfOpen,
    });
    if (!toggleIfOpen) setActiveAction(action);
  }, [activeAction]);

  const handleContactClick = useCallback(() => requestWorkspaceAction("Contact"), [requestWorkspaceAction]);
  const handlePricingClick = useCallback(() => requestWorkspaceAction("Pricing"), [requestWorkspaceAction]);
  const handleLanguageNavClick = useCallback(() => requestWorkspaceAction("Language"), [requestWorkspaceAction]);
  const handleDownloadNavClick = useCallback(() => requestWorkspaceAction("Download"), [requestWorkspaceAction]);
  // The Videos window opens on a film (the hero's on why-us, Pricing's on the
  // plans); once it is open, its own list switches films in place.
  const openVideo = useCallback((film: LandingFilmId) => {
    setVideoFilm(film);
    if (activeAction !== "Video") requestWorkspaceAction("Video");
  }, [activeAction, requestWorkspaceAction]);
  const heroWatch = useMemo<LandingWatchAction>(() => ({
    onClick: () => openVideo(DEFAULT_LANDING_FILM),
    onIntent: () => {
      prefetchLandingWorkspace("Video");
      // The why-us film is the window's first chunk after its own; a failed fetch retries on open.
      import("@/app/components/public/landing/whyUsFilm").catch(() => {});
    },
  }), [openVideo]);

  useEffect(() => {
    if (restoredLocaleContinuityRef.current) return;
    const continuity = consumeLandingLocaleContinuity();
    if (!continuity) return;

    // The remounted tree already carries the new locale's catalog.
    restoredLocaleContinuityRef.current = true;
    clearLandingLocaleContinuity();
    if (continuity.activeAction) requestWorkspaceAction(continuity.activeAction);
  }, [requestWorkspaceAction]);
  const handleWorkspaceReady = useCallback(() => setWorkspaceReady(true), []);
  const handleWorkspaceActionStateChange = useCallback(
    (action: LandingWorkspaceAction | null) => {
      setActiveAction(action);
      if (action !== null) {
        lastVisibleWorkspaceActionRef.current = action;
        return;
      }

      const closedAction = lastVisibleWorkspaceActionRef.current;
      lastVisibleWorkspaceActionRef.current = null;

      if (workspaceFocusRestoreFrameRef.current !== null) {
        window.cancelAnimationFrame(workspaceFocusRestoreFrameRef.current);
      }
      workspaceFocusRestoreFrameRef.current = window.requestAnimationFrame(() => {
        workspaceFocusRestoreFrameRef.current = null;
        const focusedElement = document.activeElement;
        const focusWasLost = !focusedElement || focusedElement === document.body || focusedElement === document.documentElement;
        // A trigger inside a window that minimized meanwhile (Pricing's film button) is inert.
        const target = windowFocusReturnTarget(closedAction ? workspaceTriggerRefs.current[closedAction] : null);
        if (focusWasLost && target) target.focus();
      });
    },
    []
  );

  useEffect(
    () =>
      onBackForwardCacheRestore(window, () => {
        authInFlightRef.current = false;
        setIsAuthenticating(false);
        setActiveAction((action) => (action === "Login" ? null : action));
      }),
    []
  );

  const handleLoginClick = useCallback(async () => {
    if (authInFlightRef.current) return;
    setErrorMessage(null);
    authInFlightRef.current = true;
    setActiveAction("Login");
    setLoginEntry("home");
    clearSession();
    setIsAuthenticating(true);
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      const baseUrl = isElectron
        ? API_BASE_URL
        : (typeof window !== "undefined" ? window.location.origin : process.env.NEXT_PUBLIC_DOMAIN || "");
      // Electron opens this sign-in in the system browser (openAuthPopup).
      const authorizeUrl = buildWorkosAuthorizeUrl({ baseUrl, isElectron, browserHandoff: isElectron });
      const response = await fetch(authorizeUrl, {
        cache: "no-store",
        signal: AbortSignal.timeout(PUBLIC_LANDING_REQUEST_TIMEOUT_MS),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t("Failed to get authorization URL"));
      }

      const data = await response.json();
      const authorizationUrl = parseSafeWorkosAuthorizationUrl(data.url);
      if (authorizationUrl) {
        if (isElectron) {
          const result = await openAuthPopup(authorizationUrl.toString(), data.desktopCodeVerifier);
          if (result.success && result.session) {
            if (!commitElectronLoginSession(result.session)) {
              throw new Error(t("Secure session could not be saved. Check app storage permissions and try again."));
            }
            broadcastLogin();
            // A full document load boots the dashboard on the committed session,
            // and replace keeps the sign-in page out of history.
            // in-app-browser exception: a first-party Smart Manager page in this same window, not a website.
            window.location.replace(DASHBOARD_PATH);
          } else if (result.error && result.error !== "cancelled") {
            setErrorMessage(t("Authentication failed: {error}", { error: result.error }));
          }
          authInFlightRef.current = false;
          setIsAuthenticating(false);
        } else {
          // in-app-browser exception: sign-in runs in this top-level window (WorkOS needs it; never framed).
          globalThis.location.href = authorizationUrl.toString();
        }
      } else {
        throw new Error(t("The authorization service returned an unsafe redirect URL"));
      }
    } catch (error) {
      console.error("Authorization failed:", error);
      setErrorMessage(getLandingRequestErrorMessage(error, t("Failed to initiate authentication")));
      authInFlightRef.current = false;
      setIsAuthenticating(false);
    }
  }, [t]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("start") !== "1") return;
    void handleLoginClick();
  }, [handleLoginClick]);

  const downloadMenu = useMemo<LandingDesktopDownloadMenu>(() => {
    const desktopPlatforms: Array<{ key: DesktopDownloadPlatform; label: string }> = [
      { key: "mac", label: t("macOS (Apple Silicon)") },
      { key: "mac-intel", label: t("macOS (Intel)") },
      { key: "windows", label: t("Windows") },
      { key: "linux", label: t("Linux x64 (.deb)") },
      { key: "linux-appimage", label: t("Linux x64 (AppImage)") },
    ];
    const detectedPlatform = detectedOS === "unknown" ? null : detectedOS;
    const orderedDesktopPlatforms = orderLandingDesktopDownloadItems(desktopPlatforms, detectedOS);
    const comingSoon = t("Coming soon");
    const mobilePlatforms: Array<{
      key: "ios" | "android";
      label: string;
      href: string | null;
    }> = [
      {
        key: "ios",
        label: t("iOS"),
        href: IOS_DOWNLOAD_URL,
      },
      {
        key: "android",
        label: t("Android"),
        href: ANDROID_DOWNLOAD_URL,
      },
    ];

    return {
      desktop: {
        label: t("Desktop"),
        items: orderedDesktopPlatforms.map((item) => ({
          kind: "action",
          key: item.key,
          label: downloadingPlatform === item.key ? t("Downloading...") : item.label,
          onSelect: () => downloadDesktopArtifact(item.key, { inline: true }),
          disabled: isDownloading,
          busy: downloadingPlatform === item.key,
          emphasized: detectedPlatform === item.key,
        })),
      },
      mobile: {
        label: t("Mobile"),
        items: mobilePlatforms.map((item) => item.href
          ? {
              kind: "link" as const,
              key: item.key,
              label: item.label,
              href: item.href,
            }
          : {
              kind: "unavailable" as const,
              key: item.key,
              label: item.label,
              tag: comingSoon,
            }),
      },
      web: {
        label: t("Web"),
        item: {
          kind: "action",
          key: "web",
          label: t("Use it in your browser"),
          onSelect: handleLoginClick,
          disabled: isAuthenticating,
          busy: isAuthenticating,
        },
      },
      smartManagerOS: {
        label: t("Smart Manager OS"),
        item: {
          kind: "unavailable",
          key: "smart-manager-os",
          label: t("Smart Manager OS"),
          tag: comingSoon,
        },
      },
    };
  }, [
    detectedOS,
    downloadDesktopArtifact,
    downloadingPlatform,
    handleLoginClick,
    isAuthenticating,
    isDownloading,
    t,
  ]);

  const navWindows = useMemo<LandingNavWindowsData>(() => ({
    language: {
      current: normalizedLocale,
      options: languageOptions,
      onSelect: handleLanguageSelect,
    },
    download: isElectron ? null : { menu: downloadMenu, error: downloadPanelError },
    video: { film: videoFilm, open: openVideo, select: setVideoFilm },
  }), [downloadMenu, downloadPanelError, handleLanguageSelect, languageOptions, normalizedLocale, openVideo, videoFilm]);
  const currentLanguageLabel = getNativeLanguageName(
    normalizedLocale,
    t(LANGUAGE_LABELS[normalizedLocale] || normalizedLocale.toUpperCase()),
  );

  const introOffer = introOfferNote(t);
  const heroActions: HeroAction[] = [
    // The intro offer shows once, on Pricing, where its terms are (owner 2026-10-22).
    { label: t("Pricing"), offer: introOffer, ariaLabel: introOffer ? `${t("Pricing")}, ${introOffer}` : undefined, onClick: handlePricingClick, actionKey: "Pricing" },
    { label: t("Contact"), onClick: handleContactClick, actionKey: "Contact" },
  ];

  const toNavAction = (action: HeroAction) => {
    const isActive = activeAction === action.actionKey;
    return {
      key: action.actionKey,
      label: action.label,
      onClick: action.onClick,
      disabled: action.disabled,
      pressed: isActive,
      expanded: isActive,
      busy: isActive && !workspaceReady,
      ariaLabel: action.ariaLabel,
      offer: action.offer,
      onIntent: () => prefetchLandingWorkspace(action.actionKey),
    };
  };

  // Deferred so a layout switch keeps the current landing on screen while the
  // other layout's chunk loads, instead of suspending the page.
  const isLandingMobile = useDeferredValue(
    initialIsMobile || isMobile || viewportSize.width < LANDING_DESKTOP_MIN_WIDTH,
  );
  // Windows open over the hero; its copy steps back while one is open so no
  // window ever leaves the headline half covered.
  const heroCopyRecedes = isLandingWorkspaceAction(activeAction);
  // On a short desktop stage the open window gets the provider rail's height; the legal links stay.
  const footerRailCollapsed = !isLandingMobile && isLandingFooterRailCollapsed({
    windowOpen: heroCopyRecedes,
    stageHeight: layoutViewport.height,
  });
  // The sphere and the copy under it are one stack between the nav and the
  // provider rail; on a short stage the sphere gives way, then the copy's lines.
  const heroLayout = getDesktopHeroLayout({
    width: layoutViewport.width,
    height: layoutViewport.height,
    copyParts: heroCopy.parts,
  });
  // Until the window and the copy are measured (before the first client
  // paint), the copy is placed by the same layout in CSS and shows from the
  // server render; the sphere is client-only and fades in once placed.
  const heroPlacing = !viewportMeasured || heroCopy.parts === null;
  const availableOrbitWidth = Math.max(300, layoutViewport.width - 220);
  const availableOrbitHeight = Math.max(300, layoutViewport.height - 280);
  const orbitDiameter = clampValue(Math.min(availableOrbitWidth, availableOrbitHeight), 300, 760);
  const centerOrbSize = heroLayout.orb.diameter;
  const centerGlowSize = clampValue(orbitDiameter * 0.68, 320, 500);
  const backdropHaloSize = clampValue(orbitDiameter * 1.4, 520, 900);
  const orbitCenterLeft = `${heroLayout.orb.xShare * 100}%`;
  const orbitAnchorStyle = { top: heroLayout.orb.y, left: orbitCenterLeft } as const;

  // Public windows materialize from the hero sphere's nodes (it registers as
  // a particle source), the same entrance AI-rendered windows get in the
  // platform, and ripple on landing.
  useAmbientPulseRings(ambientPulseFieldRef, !isLandingMobile);

  const navActions = heroActions.map(toNavAction);
  const languageNavAction = LANDING_LANGUAGE_PICKER_ENABLED
    ? toNavAction({ label: currentLanguageLabel, ariaLabel: `${t("Language")}: ${currentLanguageLabel}`, onClick: handleLanguageNavClick, actionKey: "Language" })
    : undefined;
  const downloadNavAction = isElectron ? undefined : toNavAction({ label: t("Download"), onClick: handleDownloadNavClick, actionKey: "Download" });
  const signIn = {
    busyLabel: t("Authorizing..."),
    isBusy: isAuthenticating,
    label: t("Get started"),
    onClick: handleLoginClick,
  };
  // Both layouts open the same public windows; the compact one frames them
  // full-width under its header.
  const workspace = workspaceMounted && (
    <>
      <LandingNavWindowsContext.Provider value={navWindows}>
        <LandingPublicWorkspace
          command={workspaceCommand}
          layout={isLandingMobile ? "compact" : "desktop"}
          onActionStateChange={handleWorkspaceActionStateChange}
          onReady={handleWorkspaceReady}
        />
      </LandingNavWindowsContext.Provider>
      <span hidden data-workspace-ready={workspaceReady} />
    </>
  );

  if (isLandingMobile) {
    return (
      <MobileOrbLanding
        t={t}
        errorMessage={errorMessage}
        onDismissError={dismissLandingError}
        viewport={layoutViewport}
        screenHeight={viewportSize.height}
        viewportMeasured={viewportMeasured}
        heroRecedes={heroCopyRecedes}
        actions={navActions}
        language={languageNavAction}
        download={downloadNavAction}
        signIn={signIn}
        watch={heroWatch}
        workspace={workspace}
      />
    );
  }

  return (
    <div className="pointer-events-none min-h-screen w-full bg-[#020813] text-white overflow-hidden relative font-sans selection:bg-cyan-500/30">
      {/* Background Effects */}
      <ThreeDNebula />
      <div ref={ambientPulseFieldRef} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[1] overflow-hidden" />
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Subtle Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(to right, #4f4f4f 1px, transparent 1px), linear-gradient(to bottom, #4f4f4f 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        {/* Brighter bridge fog around the hero focal point */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            ...orbitAnchorStyle,
            transform: "translate(-50%, -50%)",
            width: backdropHaloSize,
            height: backdropHaloSize,
            background:
              "radial-gradient(circle, rgba(56,189,248,0.24) 0%, rgba(139,92,246,0.16) 36%, rgba(244,114,182,0.12) 64%, rgba(2,8,19,0) 88%)",
            filter: "blur(136px)",
          }}
        />
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            ...orbitAnchorStyle,
            transform: "translate(-50%, -50%)",
            width: orbitDiameter * 1.2,
            height: Math.max(120, orbitDiameter * 0.32),
            background:
              "linear-gradient(90deg, rgba(45,212,191,0.32) 0%, rgba(56,189,248,0.36) 35%, rgba(167,139,250,0.4) 65%, rgba(244,114,182,0.32) 100%)",
            filter: "blur(72px)",
          }}
        />
      </div>

      {workspace}

      {/* Cinematic hero scene: asymmetric image reveal + particle flow */}
      <HomeHeroScene
        orbRef={orbContainerRef}
        orbDiameter={centerOrbSize}
        orbCenterY={heroLayout.orb.y}
      />

      {/* Main Content Container */}
      <div
        className="relative w-full flex flex-col p-4 sm:p-8 overflow-hidden pointer-events-none"
        style={{ height: `calc(100vh - ${CONSENT_BAR_RESERVE_BOTTOM})` }}
      >
        {/* Top Navigation */}
        <header className="fixed right-4 top-4 z-[200] isolate pointer-events-auto [transform:translateZ(0)] [will-change:transform] sm:right-8 sm:top-6">
          <LandingDesktopNavigation
            actions={navActions}
            language={languageNavAction}
            download={downloadNavAction}
            signIn={signIn}
          />
        </header>

        {/* Central Visualization Area: laid out in stage px, like the backdrop behind it */}
        <main className="absolute inset-0">
          {errorMessage ? (
            <LandingErrorBanner
              message={errorMessage}
              onDismiss={dismissLandingError}
              dismissLabel={t("Dismiss")}
              className="pointer-events-auto absolute left-1/2 top-12 z-[120] w-max max-w-[min(560px,90vw)] -translate-x-1/2"
            />
          ) : null}

          {/* Central Orb */}
          <div
            ref={orbContainerRef}
            data-hero-orb-placing={heroPlacing || undefined}
            className={`absolute z-20 flex items-center justify-center motion-safe:transition-opacity motion-safe:duration-300 ${heroPlacing ? "opacity-0" : "opacity-100"}`}
            style={{
              ...orbitAnchorStyle,
              width: centerOrbSize,
              height: centerOrbSize,
              transform: "translate(-50%, -50%)",
            }}
          >
            {/* Faint glow in the sphere's own cyan, behind its dots */}
            <div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: centerGlowSize,
                height: centerGlowSize,
                background:
                  "radial-gradient(circle, rgba(0,212,255,0.16) 0%, rgba(0,212,255,0.06) 46%, rgba(0,212,255,0) 72%)",
                filter: "blur(48px)",
              }}
            />

            {/* The platform's own neural sphere */}
            <HeroNeuralSphere size={centerOrbSize} audioStateRef={heroVoiceRef} />

            {/* Foreground Content (Text & Logo) */}
            <div
              data-hero-copy-receded={heroCopyRecedes}
              className={`relative z-10 flex flex-col items-center justify-center pointer-events-none max-w-[90vw] sm:max-w-none motion-safe:transition-opacity motion-safe:duration-300 ${heroCopyRecedes ? "opacity-0" : "opacity-100"}`}
            >
              <Image
                alt={t("Smart Manager logo")}
                src={SMART_MANAGER_LOGO_URL}
                width={64}
                height={64}
                loading="eager"
                fetchPriority="high"
                draggable={false}
                className="h-7 w-7 drop-shadow-[0_0_18px_rgba(255,255,255,0.65)] sm:h-16 sm:w-16"
              />
              <p className="mt-0.5 text-[10px] font-semibold tracking-[0.03em] text-white/95 drop-shadow-md sm:mt-1 sm:text-[15px]">
                Smart Manager
              </p>
            </div>
          </div>

          {/* What it is and who it's for (Get started is in the nav); steps back while a window is open */}
          {/* Places the copy from SSR until it has been measured */}
          {heroPlacing && <style>{DESKTOP_HERO_PROVISIONAL_COPY_CSS}</style>}
          <LandingHeroCopy
            ref={heroCopy.ref}
            t={t}
            size={heroLayout.copy.size}
            receded={heroCopyRecedes}
            watch={heroWatch}
            provisional={heroPlacing}
            className="absolute z-20 -translate-x-1/2"
            style={
              heroPlacing
                ? { left: orbitCenterLeft }
                : { left: orbitCenterLeft, top: heroLayout.copy.top, width: heroLayout.copy.width }
            }
          />

        </main>

        {/* Footer */}
        <footer
          data-landing-footer="true"
          data-rail-collapsed={footerRailCollapsed || undefined}
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[82px] ${footerRailCollapsed ? "sm:h-11" : "sm:h-24"}`}
        >
          <div className="absolute inset-0 overflow-hidden backdrop-blur-sm">
            <div
              className="absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, rgba(34,211,238,0) 0%, rgba(125,211,252,0.3) 20%, rgba(255,255,255,0.22) 50%, rgba(125,211,252,0.3) 80%, rgba(34,211,238,0) 100%)",
              }}
            />
            <div className="absolute inset-x-0 top-0 h-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0))]" />
            <div className="absolute inset-y-0 left-0 w-40 bg-[radial-gradient(circle_at_left,rgba(34,211,238,0.12),transparent_72%)]" />
            <div className="absolute inset-y-0 right-0 w-40 bg-[radial-gradient(circle_at_right,rgba(167,139,250,0.12),transparent_72%)]" />
            {footerRailCollapsed ? null : <IntegrationProviderRail />}
          </div>
          <div className="pointer-events-auto absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 px-4 text-[10px] tracking-wide text-white/60">
            <div className="flex items-center justify-center gap-1">
            <p>
              © {new Date().getFullYear()} SmartManager LLC • {t("All rights reserved")} • {t(PRIVACY_PROMISE)}
            </p>
            <LandingCompanyInfo t={t} />
          </div>
            <div className="flex items-center gap-1.5">
              <Link href="/privacy-policy" className="inline-flex min-h-11 items-center px-1 transition-colors hover:text-white/70">
                {t("Privacy Policy")}
              </Link>
              <span>•</span>
              <Link href="/privacy-policy#s-notice-at-collection" className="inline-flex min-h-11 items-center px-1 transition-colors hover:text-white/70">
                {t("Notice at Collection")}
              </Link>
              <span>•</span>
              <Link href="/terms" className="inline-flex min-h-11 items-center px-1 transition-colors hover:text-white/70">
                {t("Terms & Conditions")}
              </Link>
              <span className="hidden md:inline">•</span>
              <Link href="/security" prefetch={false} className="hidden min-h-11 items-center px-1 transition-colors hover:text-white/70 md:inline-flex">
                {t("Security")}
              </Link>
              <span className="hidden md:inline">•</span>
              <Link href="/cookies" prefetch={false} className="hidden min-h-11 items-center px-1 transition-colors hover:text-white/70 md:inline-flex">
                {t("Cookies")}
              </Link>
              <span className="hidden md:inline">•</span>
              <Link href="/subprocessors" prefetch={false} className="hidden min-h-11 items-center px-1 transition-colors hover:text-white/70 md:inline-flex">
                {t("Subprocessors")}
              </Link>
              <span className="hidden md:inline">•</span>
              <Link href="/help" prefetch={false} className="hidden min-h-11 items-center px-1 transition-colors hover:text-white/70 md:inline-flex">
                {t("Help")}
              </Link>
              <span className="hidden md:inline">•</span>
              <Link href="/status" prefetch={false} className="hidden min-h-11 items-center px-1 transition-colors hover:text-white/70 md:inline-flex">
                {t("Status")}
              </Link>
              <span className="hidden md:inline">•</span>
              <Link href="/changelog" prefetch={false} className="hidden min-h-11 items-center px-1 transition-colors hover:text-white/70 md:inline-flex">
                {t("Changelog")}
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
